"use client";

import { useEffect, useMemo, useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import {
  buildRubricRows,
  rubricToMarkdown,
  rubricToTsv,
} from "@/lib/tool-kit/study/rubric-table";

interface Criterion {
  id: string;
  name: string;
  points: string;
  cells: string[];
}

interface TemplateCriterion {
  name: string;
  points: number;
  cells: string[];
}

interface RubricTemplate {
  id: string;
  label: string;
  criteria: TemplateCriterion[];
}

/**
 * The numbers and the per-template criterion order — not translatable, so they
 * stay in code. `slug` keys into the strings object's `criterionNames`
 * (by position) and `cells` (as `${templateId}:${slug}`); it is never rendered.
 */
interface CriterionSpec {
  slug: string;
  points: number;
}

interface TemplateSpec {
  id: string;
  criteria: CriterionSpec[];
}

const TEMPLATE_SPECS: TemplateSpec[] = [
  {
    id: "essay",
    criteria: [
      { slug: "thesis-and-focus", points: 20 },
      { slug: "organization", points: 20 },
      { slug: "evidence-and-support", points: 25 },
      { slug: "analysis", points: 20 },
      { slug: "grammar-and-mechanics", points: 15 },
    ],
  },
  {
    id: "presentation",
    criteria: [
      { slug: "content-accuracy", points: 25 },
      { slug: "organization", points: 20 },
      { slug: "delivery", points: 25 },
      { slug: "visual-aids", points: 15 },
      { slug: "time-management", points: 15 },
    ],
  },
  {
    id: "group-project",
    criteria: [
      { slug: "collaboration", points: 25 },
      { slug: "quality-of-work", points: 25 },
      { slug: "individual-contribution", points: 20 },
      { slug: "meeting-deadlines", points: 15 },
      { slug: "presentation-of-results", points: 15 },
    ],
  },
  {
    id: "lab-report",
    criteria: [
      { slug: "hypothesis-and-purpose", points: 15 },
      { slug: "procedure-and-methods", points: 20 },
      { slug: "data-and-results", points: 25 },
      { slug: "analysis-and-conclusion", points: 25 },
      { slug: "format-and-clarity", points: 15 },
    ],
  },
];

/**
 * Reassembles the templates the widget renders from `TEMPLATE_SPECS` (ids,
 * order, points — code) plus the strings object's `templateLabels`,
 * `criterionNames` and `cells` (text — translatable). A name or cell list
 * missing from a translation falls back to the slug/empty list rather than
 * throwing, the same degrade-to-English behaviour `mergeToolStrings` gives
 * every other widget string.
 */
function buildTemplates(
  templateLabels: Record<string, string>,
  criterionNames: Record<string, readonly string[]>,
  cells: Record<string, readonly string[]>
): RubricTemplate[] {
  return TEMPLATE_SPECS.map((spec) => ({
    id: spec.id,
    label: templateLabels[spec.id] ?? spec.id,
    criteria: spec.criteria.map((criterion, index) => ({
      name: criterionNames[spec.id]?.[index] ?? criterion.slug,
      points: criterion.points,
      cells: (cells[`${spec.id}:${criterion.slug}`] ?? []).slice(),
    })),
  }));
}

/** English strings. A translated content file overrides these through `ui`. */
export const RUBRIC_GENERATOR_STRINGS = {
  startFromTemplate: "Start from a template",
  startBlank: "Start blank",
  rubricTitle: "Rubric title",
  titlePlaceholder: "Essay Rubric",
  criterion: "Criterion",
  points: "Points",
  remove: "Remove",
  performanceLevels: "Performance level",
  levelNameLabel: "Performance level {number} name",
  criterionNameLabel: "Criterion {number} name",
  criterionPlaceholder: "Criterion",
  descriptorPlaceholder: "Descriptor",
  cellLabel: "{criterion} — {level} descriptor",
  levelFallback: "level {number}",
  criterionFallback: "Criterion {number}",
  pointsLabel: "{criterion} points",
  removeLabel: "Remove {name}",
  criterionFallbackAria: "criterion {number}",
  addCriterion: "+ Add criterion",
  print: "Print",
  copied: "Copied!",
  copyAsMarkdown: "Copy as Markdown",
  copyAsTabSeparated: "Copy as Tab-Separated",
  preview: "Preview",
  rubricFallback: "Rubric",
  total: "Total",
  blankTitle: "Rubric",
  templateTitle: "{label} Rubric",
  replaceConfirm:
    "Replace the rubric you are editing with this one? Your changes will be lost.",
  defaultLevels: ["Excellent", "Good", "Satisfactory", "Needs Improvement"],
  templateLabels: {
    essay: "Essay",
    presentation: "Presentation",
    "group-project": "Group Project",
    "lab-report": "Lab Report",
  },
  criterionNames: {
    essay: [
      "Thesis & Focus",
      "Organization",
      "Evidence & Support",
      "Analysis",
      "Grammar & Mechanics",
    ],
    presentation: [
      "Content Accuracy",
      "Organization",
      "Delivery",
      "Visual Aids",
      "Time Management",
    ],
    "group-project": [
      "Collaboration",
      "Quality of Work",
      "Individual Contribution",
      "Meeting Deadlines",
      "Presentation of Results",
    ],
    "lab-report": [
      "Hypothesis & Purpose",
      "Procedure & Methods",
      "Data & Results",
      "Analysis & Conclusion",
      "Format & Clarity",
    ],
  },
  cells: {
    "essay:thesis-and-focus": [
      "Clear, insightful thesis sustained throughout the essay.",
      "Clear thesis with only minor drift in places.",
      "Thesis present but vague or inconsistently followed.",
      "No identifiable thesis or central focus.",
    ],
    "essay:organization": [
      "Logical structure with smooth, purposeful transitions.",
      "Mostly logical; transitions occasionally abrupt.",
      "Some organization, but ideas jump around.",
      "Little discernible structure or paragraphing.",
    ],
    "essay:evidence-and-support": [
      "Strong, well-cited evidence supports every claim.",
      "Solid evidence with a few unsupported claims.",
      "Limited or loosely related evidence.",
      "Claims are largely unsupported.",
    ],
    "essay:analysis": [
      "Original analysis that goes well beyond summary.",
      "Good analysis with some summary padding.",
      "Mostly summary with occasional analysis.",
      "Retells sources without any analysis.",
    ],
    "essay:grammar-and-mechanics": [
      "Virtually error-free prose.",
      "A few minor errors that never impede meaning.",
      "Frequent errors that sometimes distract the reader.",
      "Errors seriously impede readability.",
    ],
    "presentation:content-accuracy": [
      "Information is accurate, thorough, and well-sourced.",
      "Accurate with minor gaps or small errors.",
      "Several inaccuracies or shallow coverage.",
      "Content is largely inaccurate or off-topic.",
    ],
    "presentation:organization": [
      "Clear opening, logical flow, and strong conclusion.",
      "Generally clear flow with a weak opening or close.",
      "Order of ideas is hard to follow at times.",
      "No clear beginning, middle, or end.",
    ],
    "presentation:delivery": [
      "Confident, audible, well-paced; strong eye contact.",
      "Mostly confident with occasional pacing issues.",
      "Reads from notes often; uneven volume or pace.",
      "Inaudible, rushed, or read entirely from slides.",
    ],
    "presentation:visual-aids": [
      "Slides or visuals are clear and enhance every point.",
      "Visuals support most points with minor clutter.",
      "Visuals are cluttered or only loosely relevant.",
      "Visuals are missing, unreadable, or distracting.",
    ],
    "presentation:time-management": [
      "Fits the time limit with well-balanced sections.",
      "Slightly over or under time; balance mostly good.",
      "Noticeably over or under time; sections uneven.",
      "Far outside the time limit or ends abruptly.",
    ],
    "group-project:collaboration": [
      "All members contribute and decisions are shared.",
      "Most members contribute; roles mostly balanced.",
      "Uneven participation; some members disengaged.",
      "One person does the work or the group conflicts.",
    ],
    "group-project:quality-of-work": [
      "Final product is polished, complete, and accurate.",
      "Solid product with minor gaps or rough edges.",
      "Product is incomplete or has notable errors.",
      "Product is unfinished or well below expectations.",
    ],
    "group-project:individual-contribution": [
      "Each member's contribution is clear and substantial.",
      "Contributions are identifiable and adequate.",
      "Some contributions are minimal or unclear.",
      "Individual contributions cannot be identified.",
    ],
    "group-project:meeting-deadlines": [
      "All milestones and the final deadline were met.",
      "Final deadline met; a milestone slipped slightly.",
      "Several milestones missed; final work rushed.",
      "Deadlines largely ignored; work submitted late.",
    ],
    "group-project:presentation-of-results": [
      "Results are communicated clearly by every member.",
      "Results are clear; participation slightly uneven.",
      "Results are partly unclear or one-sided.",
      "Results are confusing or presented by one member.",
    ],
    "lab-report:hypothesis-and-purpose": [
      "Purpose and testable hypothesis are precisely stated.",
      "Purpose clear; hypothesis slightly vague.",
      "Purpose or hypothesis is unclear or untestable.",
      "No stated purpose or hypothesis.",
    ],
    "lab-report:procedure-and-methods": [
      "Methods are detailed enough to replicate exactly.",
      "Methods mostly complete; a step or two missing.",
      "Methods are sketchy and hard to replicate.",
      "Methods are missing or unusable.",
    ],
    "lab-report:data-and-results": [
      "Data are complete, accurate, and clearly presented.",
      "Data mostly complete; tables or units imperfect.",
      "Data are incomplete or poorly organized.",
      "Data are missing, fabricated, or unreadable.",
    ],
    "lab-report:analysis-and-conclusion": [
      "Conclusion follows from data and addresses error.",
      "Reasonable conclusion; limited error discussion.",
      "Conclusion only loosely tied to the data.",
      "No conclusion or one contradicted by the data.",
    ],
    "lab-report:format-and-clarity": [
      "Follows the required format; writing is precise.",
      "Minor format lapses; writing generally clear.",
      "Several format problems; writing often unclear.",
      "Format ignored; writing hard to follow.",
    ],
  },
} as const;

let idCounter = 0;

function newId(): string {
  idCounter += 1;
  return `c${idCounter}`;
}

function blankCriterion(): Criterion {
  return { id: newId(), name: "", points: "10", cells: ["", "", "", ""] };
}

function fromTemplate(template: RubricTemplate): Criterion[] {
  return template.criteria.map((c) => ({
    id: newId(),
    name: c.name,
    points: String(c.points),
    cells: c.cells.slice(),
  }));
}

function totalPoints(criteria: Criterion[]): number {
  return criteria.reduce((sum, c) => sum + (Number(c.points) || 0), 0);
}

type Strings = typeof RUBRIC_GENERATOR_STRINGS;

function criterionName(c: Criterion, ci: number, strings: Strings): string {
  return c.name || t(strings.criterionFallback, { number: ci + 1 });
}

export default function RubricGeneratorWidget() {
  const strings = useToolUi(RUBRIC_GENERATOR_STRINGS);
  const templates = useMemo(
    () =>
      buildTemplates(
        strings.templateLabels,
        strings.criterionNames,
        strings.cells
      ),
    [strings.templateLabels, strings.criterionNames, strings.cells]
  );
  const [levels, setLevels] = useState<string[]>(() => [
    ...strings.defaultLevels,
  ]);
  const [criteria, setCriteria] = useState<Criterion[]>(() =>
    fromTemplate(templates[0] as RubricTemplate)
  );
  const [title, setTitle] = useState(() =>
    t(strings.templateTitle, { label: (templates[0] as RubricTemplate).label })
  );
  const [dirty, setDirty] = useState(false);
  const [copied, setCopied] = useState<"md" | "tsv" | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const hasContent = criteria.some((c) => c.name.trim().length > 0);
  const rows = buildRubricRows(levels, criteria, {
    criterion: strings.criterion,
    points: strings.points,
  });

  function confirmReplace(): boolean {
    // biome-ignore lint/suspicious/noAlert: a native confirm is the lightest guard against losing edits
    return !dirty || window.confirm(strings.replaceConfirm);
  }

  function applyTemplate(template: RubricTemplate) {
    if (!confirmReplace()) {
      return;
    }
    setCriteria(fromTemplate(template));
    setLevels([...strings.defaultLevels]);
    setTitle(t(strings.templateTitle, { label: template.label }));
    setDirty(false);
    setCopied(null);
  }

  function startBlank() {
    if (!confirmReplace()) {
      return;
    }
    setDirty(false);
    setCriteria([blankCriterion(), blankCriterion(), blankCriterion()]);
    setLevels([...strings.defaultLevels]);
    setTitle(strings.blankTitle);
    setCopied(null);
  }

  function setLevelAt(index: number, value: string) {
    setDirty(true);
    setLevels((prev) => prev.map((l, i) => (i === index ? value : l)));
  }

  function updateCriterion(id: string, patch: Partial<Criterion>) {
    setDirty(true);
    setCriteria((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...patch } : c))
    );
  }

  function updateCell(id: string, cellIndex: number, value: string) {
    setDirty(true);
    setCriteria((prev) =>
      prev.map((c) => {
        if (c.id !== id) {
          return c;
        }
        const cells = c.cells.slice();
        cells[cellIndex] = value;
        return { ...c, cells };
      })
    );
  }

  function addCriterion() {
    setDirty(true);
    setCriteria((prev) => [...prev, blankCriterion()]);
  }

  function removeCriterion(id: string) {
    setDirty(true);
    setCriteria((prev) =>
      prev.length > 1 ? prev.filter((c) => c.id !== id) : prev
    );
  }

  function handleCopy(kind: "md" | "tsv") {
    const text =
      kind === "md" ? rubricToMarkdown(title, rows) : rubricToTsv(title, rows);
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <fieldset
      aria-busy={hydrated ? undefined : "true"}
      className="contents"
      disabled={!hydrated}
    >
      {/* Template picker */}
      <section className="space-y-3 print:hidden">
        <p className="font-medium text-foreground text-sm">
          {strings.startFromTemplate}
        </p>
        <div className="flex flex-wrap gap-3">
          {templates.map((tpl) => (
            <button
              className="rounded-lg border border-border bg-card px-4 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
              key={tpl.id}
              onClick={() => applyTemplate(tpl)}
              type="button"
            >
              {tpl.label}
            </button>
          ))}
          <button
            className="rounded-lg border border-border border-dashed bg-card px-4 py-2 font-medium text-muted-foreground text-sm transition-colors hover:bg-muted hover:text-foreground"
            onClick={startBlank}
            type="button"
          >
            {strings.startBlank}
          </button>
        </div>
      </section>

      {/* Editor */}
      <section className="space-y-4 print:hidden">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="rubric-title"
          >
            {strings.rubricTitle}
          </label>
          <input
            className="w-full max-w-md rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="rubric-title"
            onChange={(e) => {
              setTitle(e.target.value);
              setDirty(true);
            }}
            placeholder={strings.titlePlaceholder}
            type="text"
            value={title}
          />
        </div>
        <div className="space-y-4 sm:hidden">
          <div className="space-y-3 rounded-xl border border-border bg-muted/20 p-4">
            <p className="font-medium text-foreground text-sm">
              {strings.performanceLevels}
            </p>
            {levels.map((level, li) => (
              <label
                className="block space-y-1 text-muted-foreground text-xs"
                key={`mobile-level-${li + 1}`}
              >
                {t(strings.levelFallback, { number: li + 1 })}
                <input
                  className="w-full rounded-lg border border-border bg-card px-3 py-2.5 font-medium text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  onChange={(e) => setLevelAt(li, e.target.value)}
                  type="text"
                  value={level}
                />
              </label>
            ))}
          </div>
          {criteria.map((criterion, criterionIndex) => (
            <fieldset
              className="space-y-4 rounded-xl border border-border bg-card p-4"
              key={`mobile-${criterion.id}`}
            >
              <legend className="px-1 font-medium text-foreground text-sm">
                {`${strings.criterion} ${criterionIndex + 1}`}
              </legend>
              <label className="block space-y-1 text-muted-foreground text-xs">
                {strings.criterion}
                <input
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5 font-medium text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  onChange={(e) =>
                    updateCriterion(criterion.id, { name: e.target.value })
                  }
                  placeholder={strings.criterionPlaceholder}
                  type="text"
                  value={criterion.name}
                />
              </label>
              {criterion.cells.map((cell, cellIndex) => (
                <label
                  className="block space-y-1 text-muted-foreground text-xs"
                  key={`${criterion.id}-mobile-${cellIndex + 1}`}
                >
                  {levels[cellIndex] ??
                    t(strings.levelFallback, { number: cellIndex + 1 })}
                  <textarea
                    className="min-h-24 w-full resize-y rounded-lg border border-border bg-background px-3 py-2.5 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    onChange={(e) =>
                      updateCell(criterion.id, cellIndex, e.target.value)
                    }
                    placeholder={strings.descriptorPlaceholder}
                    value={cell}
                  />
                </label>
              ))}
              <div className="flex items-end gap-3">
                <label className="min-w-0 flex-1 space-y-1 text-muted-foreground text-xs">
                  {strings.points}
                  <input
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    inputMode="numeric"
                    onChange={(e) =>
                      updateCriterion(criterion.id, { points: e.target.value })
                    }
                    type="text"
                    value={criterion.points}
                  />
                </label>
                <button
                  className="h-11 rounded-lg border border-border bg-background px-4 text-muted-foreground text-sm transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                  disabled={criteria.length <= 1}
                  onClick={() => removeCriterion(criterion.id)}
                  type="button"
                >
                  {strings.remove}
                </button>
              </div>
            </fieldset>
          ))}
        </div>
        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full min-w-[900px] border-collapse text-sm">
            <thead>
              <tr>
                <th className="p-1 text-left" scope="col">
                  <span className="px-2 font-medium text-foreground text-sm">
                    {strings.criterion}
                  </span>
                </th>
                {levels.map((level, li) => (
                  <th className="p-1" key={`level-${li + 1}`} scope="col">
                    <input
                      aria-label={t(strings.levelNameLabel, { number: li + 1 })}
                      className="w-full rounded-lg border border-border bg-card px-2 py-1.5 text-center font-medium text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      onChange={(e) => setLevelAt(li, e.target.value)}
                      type="text"
                      value={level}
                    />
                  </th>
                ))}
                <th className="p-1" scope="col">
                  <span className="px-2 font-medium text-foreground text-sm">
                    {strings.points}
                  </span>
                </th>
                <th className="p-1" scope="col">
                  <span className="sr-only">{strings.remove}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {criteria.map((c, ci) => (
                <tr className="align-top" key={c.id}>
                  <td className="p-1">
                    <input
                      aria-label={t(strings.criterionNameLabel, {
                        number: ci + 1,
                      })}
                      className="w-40 rounded-lg border border-border bg-card px-2 py-1.5 font-medium text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      onChange={(e) =>
                        updateCriterion(c.id, { name: e.target.value })
                      }
                      placeholder={strings.criterionPlaceholder}
                      type="text"
                      value={c.name}
                    />
                  </td>
                  {c.cells.map((cell, celli) => (
                    <td className="p-1" key={`${c.id}-${celli + 1}`}>
                      <textarea
                        aria-label={t(strings.cellLabel, {
                          criterion: criterionName(c, ci, strings),
                          level:
                            levels[celli] ??
                            t(strings.levelFallback, { number: celli + 1 }),
                        })}
                        className="min-h-[76px] w-full resize-y rounded-lg border border-border bg-card px-2 py-1.5 text-foreground text-xs leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        onChange={(e) =>
                          updateCell(c.id, celli, e.target.value)
                        }
                        placeholder={strings.descriptorPlaceholder}
                        value={cell}
                      />
                    </td>
                  ))}
                  <td className="p-1">
                    <input
                      aria-label={t(strings.pointsLabel, {
                        criterion: criterionName(c, ci, strings),
                      })}
                      className="w-16 rounded-lg border border-border bg-card px-2 py-1.5 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      inputMode="numeric"
                      onChange={(e) =>
                        updateCriterion(c.id, { points: e.target.value })
                      }
                      type="text"
                      value={c.points}
                    />
                  </td>
                  <td className="p-1">
                    <button
                      aria-label={t(strings.removeLabel, {
                        name:
                          c.name ||
                          t(strings.criterionFallbackAria, { number: ci + 1 }),
                      })}
                      className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-muted-foreground text-sm transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                      disabled={criteria.length <= 1}
                      onClick={() => removeCriterion(c.id)}
                      type="button"
                    >
                      &minus;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          className="rounded-lg border border-border bg-card px-4 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
          onClick={addCriterion}
          type="button"
        >
          {strings.addCriterion}
        </button>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 print:hidden">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!hasContent}
          onClick={() => window.print()}
          type="button"
        >
          {strings.print}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={!hasContent}
          onClick={() => handleCopy("md")}
          type="button"
        >
          {copied === "md" ? strings.copied : strings.copyAsMarkdown}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={!hasContent}
          onClick={() => handleCopy("tsv")}
          type="button"
        >
          {copied === "tsv" ? strings.copied : strings.copyAsTabSeparated}
        </button>
      </div>

      {/* Preview (printable) */}
      {hasContent && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg print:hidden">
            {strings.preview}
          </h2>
          <p className="hidden font-semibold text-foreground text-xl print:block">
            {title || strings.rubricFallback}
          </p>
          <div className="overflow-x-auto rounded-lg border border-border print:overflow-visible print:rounded-none">
            <table className="w-full min-w-[760px] text-sm print:min-w-0">
              <thead className="bg-muted/50">
                <tr>
                  <th className="border-border border-b px-3 py-2 text-left font-medium text-foreground">
                    {strings.criterion}
                  </th>
                  {levels.map((level, li) => (
                    <th
                      className="border-border border-b border-l px-3 py-2 text-left font-medium text-foreground"
                      key={`preview-level-${li + 1}`}
                    >
                      {level}
                    </th>
                  ))}
                  <th className="border-border border-b border-l px-3 py-2 text-left font-medium text-foreground">
                    {strings.points}
                  </th>
                </tr>
              </thead>
              <tbody>
                {criteria.map((c) => (
                  <tr className="border-border border-t align-top" key={c.id}>
                    <td className="px-3 py-2 font-medium text-foreground">
                      {c.name}
                    </td>
                    {c.cells.map((cell, celli) => (
                      <td
                        className="border-border border-l px-3 py-2 text-muted-foreground text-xs leading-relaxed print:text-foreground"
                        key={`${c.id}-preview-${celli + 1}`}
                      >
                        {cell}
                      </td>
                    ))}
                    <td className="border-border border-l px-3 py-2 text-muted-foreground print:text-foreground">
                      {c.points}
                    </td>
                  </tr>
                ))}
                <tr className="border-border border-t bg-muted/30">
                  <td
                    className="px-3 py-2 font-medium text-foreground"
                    colSpan={levels.length + 1}
                  >
                    {strings.total}
                  </td>
                  <td className="border-border border-l px-3 py-2 font-medium text-foreground">
                    {totalPoints(criteria)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}
    </fieldset>
  );
}
